import { Range, Root, Thumb, Track } from "@radix-ui/react-slider";
import type { ScreenerFilterData } from "epsm-api-types/models";
import { useState } from "react";
import {
	type FilterDataContextProps,
	useFilterData,
} from "@/context/FilterDataContext";
import { getUnixTime, ONE_DAY_IN_MS } from "@/lib/utils";
import { findClosestValue } from "../../../lib/helpers/findClosestValue";
import { isEmptyInputValue } from "../../../lib/helpers/isEmptyInputValue";

export const RangePicker = () => {
	const { value, setValue, filterData, isDate, hasRangePickerValueChangedRef } =
		useFilterData() as {
			filterData: ScreenerFilterData;
		} & FilterDataContextProps;

	const initialMin = isDate() ? getUnixTime(String(filterData?.items[0])) : 0;
	const initialMax = isDate()
		? getUnixTime(String(filterData?.items[filterData?.items?.length - 1]))
		: filterData?.items?.length - 1;

	const minIndex = isEmptyInputValue(value?.min)
		? 0
		: filterData?.items?.findIndex(
				(item) =>
					String(item) ===
					findClosestValue(+value?.min, filterData?.items as number[]),
			);
	const maxIndex = isEmptyInputValue(value?.max)
		? filterData?.items?.length - 1
		: filterData?.items?.findIndex(
				(item) =>
					String(item) ===
					findClosestValue(+value?.max, filterData?.items as number[]),
			);

	const [activeThumb, setActiveThumb] = useState("");

	const calculateStep = () => {
		if (isDate()) return ONE_DAY_IN_MS;
		return 1;
	};
	const handleValueChange = (newThumbs: number[]) => {
		const currentValues = newThumbs.map((thumbIndex) => {
			if (isDate()) return String(thumbIndex);
			return String(filterData.items[thumbIndex]);
		});

		const [minCurrValue, maxCurrValue] = currentValues;

		if (activeThumb === "min") {
			const closestMinValue = findClosestValue(
				+minCurrValue,
				filterData.items as number[],
			);
			setValue({
				...value,
				min: isDate() ? minCurrValue : String(closestMinValue),
			});
		} else if (activeThumb === "max") {
			const closestMaxValue = findClosestValue(
				+maxCurrValue,
				filterData.items as number[],
			);
			setValue({
				...value,
				max: isDate() ? maxCurrValue : String(closestMaxValue),
			});
		}

		hasRangePickerValueChangedRef.current = true;
	};

	return (
		<div className="w-full px-1" data-testid="range-picker">
			<Root
				className="relative flex items-center mb-4 select-none touch-none"
				min={initialMin}
				max={initialMax}
				step={calculateStep()}
				value={isDate() ? [+value?.min, +value?.max] : [minIndex, maxIndex]}
				onValueChange={handleValueChange}
				onValueCommit={() => setActiveThumb("")}
			>
				<Track className="relative grow h-0.5 bg-[#2d2d2d] rounded-full">
					<Range className="absolute h-full bg-[var(--gray)] rounded-full" />
				</Track>

				{["min", "max"].map((thumb) => (
					<Thumb
						key={thumb}
						className="block w-2 h-2 bg-[var(--gray)] rounded-full cursor-pointer"
						aria-label={thumb}
						onPointerDown={() => setActiveThumb(thumb)}
					/>
				))}
			</Root>
		</div>
	);
};
